import { apiClient } from '@/api/client';
import { useNotifications } from '@/lib/notifications';
import { queryKeys } from '@/lib/queryKeys';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export interface ImportJobProgress {
  id: number;
  status:
    | 'pending'
    | 'processing'
    | 'completed'
    | 'failed'
    | 'paused'
    | 'cancelled';
  provider: string;
  totalItems: number;
  processedItems: number;
  successfulItems: number;
  failedItems: number;
  skippedItems: number;
  percentComplete: number;
  createdAt: string;
  updatedAt: string;
}

export interface ImportJobItem {
  id: number;
  externalId: string;
  status: 'pending' | 'processing' | 'success' | 'failed' | 'skipped';
  businessId?: number;
  businessName?: string;
  error?: string;
  processedAt?: string;
}

export interface Provider {
  identifier: string;
  name: string;
  configured: boolean;
}

export const useProviders = () => {
  return useQuery({
    queryKey: [queryKeys.ADMIN_IMPORT_STATUS, 'providers'],
    queryFn: async () => {
      const response = await apiClient.GET('/admin/import/jobs/providers');
      if (response.error) throw new Error('Failed to fetch providers');
      return response.data as Provider[];
    },
  });
};

export const useImportJobs = () => {
  return useQuery({
    queryKey: [queryKeys.ADMIN_IMPORT_STATUS, 'jobs'],
    queryFn: async () => {
      const response = await apiClient.GET('/admin/import/jobs');
      if (response.error) throw new Error('Failed to fetch jobs');
      return response.data as ImportJobProgress[];
    },
  });
};

export const useImportJobProgress = (
  jobId: number | null,
  enabled: boolean,
) => {
  return useQuery({
    queryKey: [queryKeys.ADMIN_IMPORT_STATUS, 'job', jobId],
    queryFn: async () => {
      if (!jobId) return null;
      const response = await apiClient.GET('/admin/import/jobs/{id}', {
        params: { path: { id: jobId } },
      });
      if (response.error) throw new Error('Failed to fetch job progress');
      return response.data as ImportJobProgress;
    },
    enabled: enabled && jobId !== null,
    refetchInterval: query =>
      query.state.data?.status === 'processing' ||
      query.state.data?.status === 'pending'
        ? 2000
        : false,
  });
};

export const useImportJobItems = (
  jobId: number | null,
  status?: string,
  enabled: boolean = true,
) => {
  return useQuery({
    queryKey: [queryKeys.ADMIN_IMPORT_STATUS, 'job', jobId, 'items', status],
    queryFn: async () => {
      if (!jobId) return [];
      const response = await apiClient.GET('/admin/import/jobs/{id}/items', {
        params: {
          path: { id: jobId },
          query: {
            status: status as
              | 'pending'
              | 'processing'
              | 'success'
              | 'failed'
              | 'skipped'
              | undefined,
          },
        },
      });
      if (response.error) throw new Error('Failed to fetch job items');
      return response.data as ImportJobItem[];
    },
    enabled: enabled && jobId !== null,
  });
};

export const useImportJobMutations = () => {
  const { addNotification } = useNotifications();
  const queryClient = useQueryClient();

  const createJob = useMutation({
    mutationFn: async (data: {
      provider: string;
      urls: string[];
      businessStatus?: string;
    }) => {
      const response = await apiClient.POST('/admin/import/jobs', {
        body: {
          provider: data.provider,
          urls: data.urls,
          businessStatus: (data.businessStatus ?? 'Active') as
            | 'Active'
            | 'Pending'
            | 'Inactive',
        },
      });
      if (response.error) throw new Error('Failed to create import job');
      return response.data as ImportJobProgress;
    },
    onSuccess: result => {
      addNotification({
        type: 'success',
        title: 'Import Job Created',
        message: `Started importing ${result.totalItems} businesses`,
      });
      queryClient.invalidateQueries({
        queryKey: [queryKeys.ADMIN_IMPORT_STATUS],
      });
    },
    onError: (error: Error) => {
      addNotification({
        type: 'error',
        title: 'Error',
        message: error.message || 'Failed to create import job',
      });
    },
  });

  const pauseJob = useMutation({
    mutationFn: async (jobId: number) => {
      const response = await apiClient.POST('/admin/import/jobs/{id}/pause', {
        params: { path: { id: jobId } },
      });
      if (response.error) throw new Error('Failed to pause job');
      return response.data as ImportJobProgress;
    },
    onSuccess: () => {
      addNotification({
        type: 'info',
        title: 'Job Paused',
        message: 'Import job has been paused',
      });
      queryClient.invalidateQueries({
        queryKey: [queryKeys.ADMIN_IMPORT_STATUS],
      });
    },
  });

  const resumeJob = useMutation({
    mutationFn: async (jobId: number) => {
      const response = await apiClient.POST('/admin/import/jobs/{id}/resume', {
        params: { path: { id: jobId } },
      });
      if (response.error) throw new Error('Failed to resume job');
      return response.data as ImportJobProgress;
    },
    onSuccess: () => {
      addNotification({
        type: 'success',
        title: 'Job Resumed',
        message: 'Import job has been resumed',
      });
      queryClient.invalidateQueries({
        queryKey: [queryKeys.ADMIN_IMPORT_STATUS],
      });
    },
  });

  const cancelJob = useMutation({
    mutationFn: async (jobId: number) => {
      const response = await apiClient.POST('/admin/import/jobs/{id}/cancel', {
        params: { path: { id: jobId } },
      });
      if (response.error) throw new Error('Failed to cancel job');
      return response.data as ImportJobProgress;
    },
    onSuccess: () => {
      addNotification({
        type: 'warning',
        title: 'Job Cancelled',
        message: 'Import job has been cancelled',
      });
      queryClient.invalidateQueries({
        queryKey: [queryKeys.ADMIN_IMPORT_STATUS],
      });
    },
  });

  const retryFailed = useMutation({
    mutationFn: async (jobId: number) => {
      const response = await apiClient.POST('/admin/import/jobs/{id}/retry', {
        params: { path: { id: jobId } },
      });
      if (response.error) throw new Error('Failed to retry failed items');
      return response.data as ImportJobProgress;
    },
    onSuccess: () => {
      addNotification({
        type: 'success',
        title: 'Retrying Failed Items',
        message: 'Retrying failed items',
      });
      queryClient.invalidateQueries({
        queryKey: [queryKeys.ADMIN_IMPORT_STATUS],
      });
    },
  });

  const deleteJob = useMutation({
    mutationFn: async (jobId: number) => {
      const response = await apiClient.DELETE('/admin/import/jobs/{id}', {
        params: { path: { id: jobId } },
      });
      if (response.error) throw new Error('Failed to delete job');
    },
    onSuccess: () => {
      addNotification({
        type: 'success',
        title: 'Job Deleted',
        message: 'Import job has been deleted',
      });
      queryClient.invalidateQueries({
        queryKey: [queryKeys.ADMIN_IMPORT_STATUS],
      });
    },
  });

  return {
    createJob,
    pauseJob,
    resumeJob,
    cancelJob,
    retryFailed,
    deleteJob,
  };
};
